import React, { useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useCart } from "../../context/CartContext";

const formatISK = (n) =>
  new Intl.NumberFormat("is-IS", { style: "currency", currency: "ISK" }).format(n);

const today = new Date();
const futureDate = new Date(today);
futureDate.setDate(today.getDate() + 3);
const minDate = futureDate.toISOString().split("T")[0];

function getPickupSlots(dateStr) {
  if (!dateStr) return [];
  const date = new Date(dateStr);
  const day = date.getDay(); // 0=Sun, 6=Sat
  const isWeekend = day === 0 || day === 6;
  const startMin = isWeekend ? 8 * 60 : 7 * 60;
  const endMin = isWeekend ? 15 * 60 : 16 * 60;
  const slots = [];
  for (let m = startMin; m <= endMin; m += 30) {
    const h = String(Math.floor(m / 60)).padStart(2, "0");
    const min = String(m % 60).padStart(2, "0");
    slots.push(`${h}:${min}`);
  }
  return slots;
}

const INPUT = "w-full h-12 px-4 bg-white border border-amber-200 rounded-xl text-stone-800 font-serif text-sm focus:outline-none focus:ring-2 focus:ring-[#C29979]/30";
const LABEL = "block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1.5";

function CartItemRow({ item, onRemove }) {
  return (
    <div className="flex items-start justify-between gap-4 bg-white rounded-xl border border-amber-100 p-4">
      <div className="min-w-0 flex-1">
        <div className="text-stone-900 font-serif text-sm font-medium">{item.label}</div>
        {item.type === "cake" && item.details?.size && (
          <div className="text-stone-400 text-xs font-serif mt-0.5">{item.details.size}</div>
        )}
        {item.type === "bite" && item.details?.description && (
          <div className="text-stone-400 text-xs font-serif mt-0.5">{item.details.description}</div>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-stone-800 font-serif text-sm font-semibold">{formatISK(item.price)}</div>
        <button
          type="button"
          onClick={() => onRemove(item.cartId)}
          className="text-stone-300 hover:text-red-400 text-xs font-serif mt-1 transition-colors"
        >
          Fjarlægja
        </button>
      </div>
    </div>
  );
}

export default function Karfa() {
  const { items, removeItem, clearCart } = useCart();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [date, setDate] = useState("");
  const [pickupTime, setPickupTime] = useState("");
  const [note, setNote] = useState("");
  const [image, setImage] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const needsImage = items.some(i => i.details?.needsImage);

  const total = useMemo(() => items.reduce((s, i) => s + i.price, 0), [items]);

  const validate = () => {
    if (items.length === 0) return "Karfan er tóm.";
    if (!name.trim()) return "Vinsamlegast fylltu út nafn.";
    if (!phone.trim()) return "Vinsamlegast fylltu út símanúmer.";
    if (phone.replace(/\D/g, "").length !== 7) return "Símanúmer verður að vera 7 tölustafir.";
    if (!email.trim() || !email.includes("@")) return "Vinsamlegast sláðu inn gilt netfang.";
    if (!date) return "Vinsamlegast veldu dagsetningu.";
    if (date < minDate) return "Pantanir verða að berast með þriggja daga fyrirvara.";
    if (!pickupTime) return "Pöntunin verður sótt klukkan?.";
    if (needsImage && !image) return "Vinsamlegast veldu mynd fyrir afmæliskökuna.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const msg = validate();
    if (msg) { setError(msg); return; }

    setSubmitting(true);
    setError("");

    const products = items.map(item => ({ type: item.type, details: item.details }));
    const formData = new FormData();
    formData.append("id", uuidv4());
    formData.append("name", name);
    formData.append("phone", phone);
    formData.append("email", email);
    formData.append("date", date);
    formData.append("pickupTime", pickupTime);
    formData.append("products", JSON.stringify(products));
    formData.append("user_message", note);
    formData.append("payed", false);
    if (image) formData.append("image", image);

    try {
      const orderRes = await fetch("https://api.kallabakari.is/api/orders", { method: "POST", body: formData });
      const orderJson = await orderRes.json();
      if (!orderRes.ok) throw new Error(orderJson?.error || orderJson?.message || `Pöntun mistókst (${orderRes.status})`);

      const orderId = orderJson?._id || orderJson?.order?._id || orderJson?.id;
      if (!orderId) throw new Error("Pöntun skilaði ekki ID");

      const payRes = await fetch("https://api.kallabakari.is/api/payment/teya/checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId }),
      });
      const payText = await payRes.text();
      let payJson = null;
      try { payJson = JSON.parse(payText); } catch {}

      if (!payRes.ok) throw new Error(payJson?.error || payJson?.message || payText || `Greiðsla mistókst (${payRes.status})`);
      const redirectUrl = payJson?.redirectUrl || payJson?.url;
      if (!redirectUrl) throw new Error("Engin greiðsluslóð í svari");

      clearCart();
      window.location.href = redirectUrl;
    } catch (err) {
      setError(err?.message || "Villa kom upp. Reyndu aftur.");
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center px-4">
        <div className="bg-white border border-amber-200 rounded-2xl p-10 text-center max-w-sm w-full shadow-sm">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 border border-amber-200 mx-auto mb-4">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C29979" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 7V6a3 3 0 016 0v1M3 7h18l-2 13H5L3 7z"/>
            </svg>
          </div>
          <h1 className="text-stone-900 font-serif text-2xl uppercase tracking-wide mb-3">Karfan er tóm</h1>
          <p className="text-stone-500 font-serif mb-6 text-sm">Bættu vörum í körfuna frá kökusíðunni eða smáréttasíðunni.</p>
          <div className="flex flex-col gap-3">
            <a href="/kokur" className="h-12 flex items-center justify-center rounded-xl bg-[#C29979] text-white font-serif uppercase text-sm hover:bg-[#b08060] transition-colors">
              Skoða kökur
            </a>
            <a href="/smarettir" className="h-12 flex items-center justify-center rounded-xl border border-amber-200 text-stone-700 font-serif uppercase text-sm hover:border-[#C29979] hover:text-[#C29979] transition-colors">
              Skoða smárétti
            </a>
          </div>
        </div>
      </div>
    );
  }

  const cakeItems = items.filter(i => i.type === "cake");
  const biteItems = items.filter(i => i.type === "bite");
  const breadItems = items.filter(i => i.type === "bread");
  const miniItems  = items.filter(i => i.type === "minidonut");

  return (
    <div className="min-h-screen bg-[#FAF7F2] px-4 py-10">
      <div className="w-full max-w-6xl mx-auto">

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-stone-900 font-serif">Karfa</h1>
          <p className="text-stone-500 font-serif mt-1 text-sm">Fylltu út upplýsingarnar þínar til að ljúka pöntuninni.</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-start">

          {/* LEFT – contact form */}
          <div className="flex-1 min-w-0 bg-white rounded-2xl border border-amber-100 shadow p-6 md:p-8">
            <h2 className="text-stone-900 font-serif text-lg font-semibold mb-5">Upplýsingarnar þínar</h2>

            {error && (
              <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-3 text-red-700 font-serif text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="min-w-0">
                  <label className={LABEL}>Nafn</label>
                  <input type="text" className={INPUT} value={name} onChange={e => setName(e.target.value)} />
                </div>
                <div className="min-w-0">
                  <label className={LABEL}>Símanúmer</label>
                  <input type="tel" className={INPUT} value={phone} onChange={e => setPhone(e.target.value)} />
                </div>
                <div className="min-w-0">
                  <label className={LABEL}>Netfang</label>
                  <input type="email" className={INPUT} value={email} onChange={e => setEmail(e.target.value)} />
                </div>
                <div className="min-w-0">
                  <label className={LABEL}>Dagsetning</label>
                  <input
                    type="date"
                    min={minDate}
                    className={INPUT}
                    value={date}
                    onChange={e => { setDate(e.target.value); setPickupTime(""); }}
                  />
                </div>
                <div className="min-w-0">
                  <label className={LABEL}>Klukkan</label>
                  <select
                    className={INPUT}
                    value={pickupTime}
                    onChange={e => setPickupTime(e.target.value)}
                    disabled={!date}
                  >
                    <option value="">
                      {date ? "Veldu tíma" : "Veldu fyrst dagsetningu"}
                    </option>
                    {getPickupSlots(date).map(slot => (
                      <option key={slot} value={slot}>{slot}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className={LABEL}>Auka athugasemdir</label>
                <textarea
                  rows={3}
                  className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl text-stone-800 font-serif text-sm focus:outline-none focus:ring-2 focus:ring-[#C29979]/30 resize-none"
                  value={note}
                  onChange={e => setNote(e.target.value)}
                />
              </div>

              {needsImage && (
                <div>
                  <label className={LABEL}>
                    Mynd fyrir köku <span className="normal-case font-normal text-red-400">*nauðsynlegt</span>
                  </label>
                  <label className="flex flex-col items-center justify-center gap-2 w-full border-2 border-dashed border-amber-200 rounded-xl px-4 py-6 cursor-pointer hover:border-[#C29979]/60 transition-colors bg-amber-50">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#C29979" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    <span className="text-stone-500 font-serif text-sm">
                      {image ? image.name : "Smelltu til að velja mynd"}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => setImage(e.target.files?.[0] ?? null)}
                    />
                  </label>
                  {image && (
                    <img
                      src={URL.createObjectURL(image)}
                      alt="Forskoðun"
                      className="mt-3 w-32 h-32 object-cover rounded-xl border border-amber-100"
                    />
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 rounded-xl bg-[#C29979] text-white font-serif uppercase tracking-wide text-sm hover:bg-[#b08060] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? "Bíð..." : "Panta og greiða"}
              </button>
            </form>
          </div>

          {/* RIGHT – cart summary */}
          <aside className="w-full lg:w-80 flex-shrink-0 space-y-4">

            <div className="bg-white rounded-2xl border border-amber-100 shadow p-6">
              <h2 className="text-stone-900 font-serif font-semibold mb-4">Pöntunin þín</h2>

              <div className="space-y-4">
                {cakeItems.length > 0 && (
                  <div>
                    <div className="text-[#C29979] font-serif text-xs uppercase tracking-wider mb-2">Kökur</div>
                    <div className="space-y-2">
                      {cakeItems.map(item => <CartItemRow key={item.cartId} item={item} onRemove={removeItem} />)}
                    </div>
                  </div>
                )}

                {biteItems.length > 0 && (
                  <div>
                    <div className="text-[#C29979] font-serif text-xs uppercase tracking-wider mb-2">Smáréttir</div>
                    <div className="space-y-2">
                      {biteItems.map(item => <CartItemRow key={item.cartId} item={item} onRemove={removeItem} />)}
                    </div>
                  </div>
                )}

                {breadItems.length > 0 && (
                  <div>
                    <div className="text-[#C29979] font-serif text-xs uppercase tracking-wider mb-2">Brauð</div>
                    <div className="space-y-2">
                      {breadItems.map(item => <CartItemRow key={item.cartId} item={item} onRemove={removeItem} />)}
                    </div>
                  </div>
                )}

                {miniItems.length > 0 && (
                  <div>
                    <div className="text-[#C29979] font-serif text-xs uppercase tracking-wider mb-2">Mini-donuts</div>
                    <div className="space-y-2">
                      {miniItems.map(item => <CartItemRow key={item.cartId} item={item} onRemove={removeItem} />)}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-5 pt-4 border-t border-amber-100 space-y-2">
                <div className="flex items-center justify-between text-stone-500 font-serif text-sm">
                  <span>Fjöldi vara</span>
                  <span className="text-stone-800">{items.length}</span>
                </div>
                <div className="flex items-center justify-between text-stone-900 font-serif font-semibold">
                  <span>Samtals</span>
                  <span className="text-[#C29979]">{formatISK(total)}</span>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={clearCart}
              className="w-full h-10 rounded-xl border border-stone-200 text-stone-400 hover:text-red-500 hover:border-red-200 font-serif uppercase text-sm transition-colors"
            >
              Tæma körfu
            </button>
          </aside>

        </div>
      </div>
    </div>
  );
}
